/**
 * HUD Renderer — screen-space + world-space game HUD.
 *
 * Delegates to composition classes for domain-specific groups:
 *   TankLabelsRenderer  — world-space per-player damage, power, names
 *   FloatingTextRenderer — world-space drift messages (ELIMINATED, etc.)
 *   TurnDialogRenderer   — turn confirmation modal
 *   NotificationsRenderer — wind shift, void rise, round start, timer
 *   MultiplayerHUDRenderer — disconnect, status, forfeit, game over prompt
 *   MomentumBarRenderer  — screen-space momentum bar
 *   ShotPanelRenderer    — screen-space shot tier panel
 *
 * Parent keeps persistent screen-space UI: title, round, stats, hints,
 * turn indicator, weapon text, game over, wind, fps, seed, lab readout.
 */

import { Container, Text, TextStyle } from 'pixi.js';
import { Input } from 'bonkjs';
import { HUD_LAYOUT, PLAYER_CARD, PLAYER_COLORS, WORLD, LOTTERY, Z } from '../../constants';
import { getCoins } from '../../progression/storage';
import { formatWind } from '../../game/wind';
import { seedToDisplay } from '../../terrain/rng';
import type { GameState } from '../../types';
import { modeUsesShotTiers } from '../../game/mode';
import { CLASSIC } from '../../constants';
import { MomentumBarRenderer } from './momentum-bar';
import { ShotPanelRenderer } from './shot-panel';
import { TankLabelsRenderer } from './tank-labels';
import { FloatingTextRenderer } from './floating-text';
import { TurnDialogRenderer } from './turn-dialog';
import { NotificationsRenderer } from './notifications';
import { MultiplayerHUDRenderer } from './multiplayer-hud';
import { PlayerCardsRenderer } from './player-cards';
import { PostMatchRenderer } from './post-match';

export class HUDRenderer {
  private worldContainer: Container;
  private uiContainer: Container;

  // Composition classes
  private tankLabels: TankLabelsRenderer;
  private floatingText: FloatingTextRenderer;
  private turnDialog: TurnDialogRenderer;
  private notifications: NotificationsRenderer;
  private multiplayerHud: MultiplayerHUDRenderer;
  private momentumBar: MomentumBarRenderer;
  private shotPanel: ShotPanelRenderer;
  private playerCards: PlayerCardsRenderer;
  private postMatch: PostMatchRenderer;

  // Screen-space persistent UI
  private titleText: Text;
  private roundText: Text;
  private hintsText: Text;
  private turnText: Text;
  private weaponText: Text;
  private gameOverText: Text;
  private windText: Text;
  private fpsText: Text;
  private seedText: Text;
  private biomeText: Text;
  private labReadout: Text;
  private coinText: Text;

  // FPS counter state
  private fpsFrames: number = 0;
  private fpsElapsed: number = 0;
  private fpsDisplay: number = 0;

  // Turn indicator punch animation
  private turnPunchTimer: number = 0;
  private prevTurnPlayer: number = -1;

  constructor(worldContainer: Container, uiContainer: Container, playerCount: number) {
    this.worldContainer = worldContainer;
    this.uiContainer = uiContainer;

    // --- Composition classes ---
    this.tankLabels = new TankLabelsRenderer(worldContainer, playerCount);
    this.floatingText = new FloatingTextRenderer(worldContainer);
    this.turnDialog = new TurnDialogRenderer(uiContainer);
    this.notifications = new NotificationsRenderer(uiContainer);
    this.multiplayerHud = new MultiplayerHUDRenderer(uiContainer);
    this.shotPanel = new ShotPanelRenderer(uiContainer, worldContainer);
    this.momentumBar = new MomentumBarRenderer(uiContainer);
    this.playerCards = new PlayerCardsRenderer(uiContainer);
    const tapKey = (code: string) => {
      Input.setVirtualKey(code, true);
      setTimeout(() => Input.setVirtualKey(code, false), 67);
    };
    this.postMatch = new PostMatchRenderer(uiContainer, tapKey);

    // --- Screen-space persistent elements ---

    // "GEOMETRY BLAST" title (top-left, cyan with glow)
    this.titleText = new Text({
      text: 'GEOMETRY BLAST',
      style: new TextStyle({
        fontFamily: '"JetBrains Mono", monospace',
        fontSize: HUD_LAYOUT.TITLE_FONT_SIZE,
        fontWeight: 'bold',
        fill: 0x00ffff,
        stroke: { color: 0x000000, width: 2 },
      }),
    });
    this.titleText.position.set(HUD_LAYOUT.TITLE_X, HUD_LAYOUT.TITLE_Y);
    this.titleText.zIndex = Z.HUD_BASE;
    uiContainer.addChild(this.titleText);

    // "Round N" (top-right)
    this.roundText = new Text({
      text: 'Round 1',
      style: new TextStyle({
        fontFamily: '"JetBrains Mono", monospace',
        fontSize: HUD_LAYOUT.ROUND_FONT_SIZE,
        fill: 0xffffff,
        stroke: { color: 0x000000, width: 2 },
      }),
    });
    this.roundText.anchor.set(1, 0);
    this.roundText.position.set(WORLD.CANVAS_WIDTH - 20, HUD_LAYOUT.ROUND_Y);
    this.roundText.zIndex = Z.HUD_BASE;
    uiContainer.addChild(this.roundText);

    // Control hints (bottom-left)
    this.hintsText = new Text({
      text: 'UP/DOWN: Aim  |  SPACE: Fire  |  A/D: Move',
      style: new TextStyle({
        fontFamily: '"JetBrains Mono", monospace',
        fontSize: HUD_LAYOUT.HINTS_FONT_SIZE,
        fill: 0x666666,
        stroke: { color: 0x000000, width: 2 },
      }),
    });
    this.hintsText.position.set(20, WORLD.CANVAS_HEIGHT - HUD_LAYOUT.HINTS_Y_OFFSET);
    this.hintsText.zIndex = Z.HUD_BASE;
    this.hintsText.visible = false;
    uiContainer.addChild(this.hintsText);

    // Turn indicator (top center)
    this.turnText = new Text({
      text: '',
      style: new TextStyle({
        fontFamily: '"JetBrains Mono", monospace',
        fontSize: HUD_LAYOUT.TURN_FONT_SIZE,
        fontWeight: 'bold',
        fill: 0xffffff,
        align: 'center',
        stroke: { color: 0x000000, width: 3 },
      }),
    });
    this.turnText.anchor.set(0.5, 0);
    this.turnText.position.set(WORLD.CANVAS_WIDTH / 2, 20);
    this.turnText.zIndex = Z.HUD_BASE;
    uiContainer.addChild(this.turnText);

    // Weapon name (below turn indicator)
    this.weaponText = new Text({
      text: '',
      style: new TextStyle({
        fontFamily: '"JetBrains Mono", monospace',
        fontSize: 16,
        fill: 0xffffff,
        align: 'center',
        stroke: { color: 0x000000, width: 3 },
      }),
    });
    this.weaponText.anchor.set(0.5, 0);
    this.weaponText.position.set(WORLD.CANVAS_WIDTH / 2, 52);
    this.weaponText.zIndex = Z.HUD_BASE;
    this.weaponText.visible = false;
    uiContainer.addChild(this.weaponText);

    // Game over text (center)
    this.gameOverText = new Text({
      text: '',
      style: new TextStyle({
        fontFamily: '"JetBrains Mono", monospace',
        fontSize: HUD_LAYOUT.GAME_OVER_FONT_SIZE,
        fontWeight: 'bold',
        fill: 0xffffff,
        align: 'center',
        stroke: { color: 0x000000, width: 5 },
      }),
    });
    this.gameOverText.anchor.set(0.5, 0.5);
    this.gameOverText.position.set(WORLD.CANVAS_WIDTH / 2, WORLD.CANVAS_HEIGHT / 2);
    this.gameOverText.zIndex = Z.HUD_BASE;
    this.gameOverText.visible = false;
    uiContainer.addChild(this.gameOverText);

    // FPS counter (top right)
    this.fpsText = new Text({
      text: 'FPS: --',
      style: new TextStyle({
        fontFamily: '"JetBrains Mono", monospace',
        fontSize: HUD_LAYOUT.FPS_FONT_SIZE,
        fill: 0x888888,
        stroke: { color: 0x000000, width: 2 },
      }),
    });
    this.fpsText.anchor.set(1, 0);
    this.fpsText.position.set(WORLD.CANVAS_WIDTH - 10, 10);
    this.fpsText.zIndex = Z.HUD_BASE;
    uiContainer.addChild(this.fpsText);

    // Wind indicator (top center, below turn indicator)
    this.windText = new Text({
      text: '',
      style: new TextStyle({
        fontFamily: '"JetBrains Mono", monospace',
        fontSize: HUD_LAYOUT.WIND_FONT_SIZE,
        fontWeight: 'bold',
        fill: 0x00ffff,
        stroke: { color: 0x000000, width: 3 },
      }),
    });
    this.windText.anchor.set(0.5, 0);
    this.windText.position.set(WORLD.CANVAS_WIDTH / 2, 55);
    this.windText.zIndex = Z.HUD_BASE;
    uiContainer.addChild(this.windText);

    // Seed display (bottom-left, subtle)
    this.seedText = new Text({
      text: 'SEED: ------',
      style: new TextStyle({
        fontFamily: '"JetBrains Mono", monospace',
        fontSize: 11,
        fill: 0x555566,
        stroke: { color: 0x000000, width: 2 },
      }),
    });
    this.seedText.position.set(20, WORLD.CANVAS_HEIGHT - 55);
    this.seedText.zIndex = Z.HUD_BASE;
    uiContainer.addChild(this.seedText);

    // Biome display (bottom-left, below seed)
    this.biomeText = new Text({
      text: '',
      style: new TextStyle({
        fontFamily: '"JetBrains Mono", monospace',
        fontSize: 11,
        fill: 0x555566,
        stroke: { color: 0x000000, width: 2 },
      }),
    });
    this.biomeText.position.set(20, WORLD.CANVAS_HEIGHT - 42);
    this.biomeText.zIndex = Z.HUD_BASE;
    uiContainer.addChild(this.biomeText);

    // Weapon Lab hit readout (training mode, top-right)
    this.labReadout = new Text({
      text: '',
      style: new TextStyle({
        fontFamily: '"JetBrains Mono", monospace',
        fontSize: 14,
        fill: 0x44ff88,
        align: 'left',
        stroke: { color: 0x000000, width: 2 },
      }),
    });
    this.labReadout.anchor.set(1, 0);
    this.labReadout.position.set(WORLD.CANVAS_WIDTH - 20, 90);
    this.labReadout.zIndex = Z.HUD_BASE;
    this.labReadout.visible = false;
    uiContainer.addChild(this.labReadout);

    // Coin counter (top-right, visible in menu screens)
    this.coinText = new Text({
      text: '',
      style: new TextStyle({
        fontFamily: '"JetBrains Mono", monospace',
        fontSize: 16,
        fontWeight: 'bold',
        fill: 0xffd700,
        stroke: { color: 0x000000, width: 3 },
      }),
    });
    this.coinText.anchor.set(1, 0);
    this.coinText.position.set(WORLD.CANVAS_WIDTH - 20, 10);
    this.coinText.zIndex = Z.HUD_BASE + 10;
    this.coinText.visible = false;
    uiContainer.addChild(this.coinText);
  }

  // --- Delegated public API ---

  showTurnDialog(playerIndex: number): void {
    this.turnDialog.show(playerIndex);
  }

  hideTurnDialog(): void {
    this.turnDialog.hide();
  }

  updateRoundStart(progress: number, roundNumber: number, visible: boolean): void {
    this.notifications.updateRoundStart(progress, roundNumber, visible);
  }

  updateTargetBlastIntro(step: number, timer: number): void {
    this.notifications.updateTargetBlastIntro(step, timer);
  }

  updateTurnTimer(secondsLeft: number): void {
    this.playerCards.updateTurnTimer(secondsLeft);
  }

  hideTurnTimer(): void {
    this.playerCards.hideTurnTimer();
  }

  triggerHealFlash(playerIndex: number): void {
    this.tankLabels.triggerHealFlash(playerIndex);
  }

  showEliminated(x: number, y: number, color: number): void {
    this.floatingText.showEliminated(x, y, color);
  }

  showVoidEliminated(x: number, y: number): void {
    this.floatingText.showVoidEliminated(x, y);
  }

  showSkyEliminated(x: number, y: number, color: number): void {
    this.floatingText.showSkyEliminated(x, y, color);
  }

  // --- Main update ---

  update(state: GameState, dt: number): void {
    // Hide all HUD elements during menu phases
    const isMenu = state.phase === 'title_screen' || state.phase === 'mode_select' || state.phase === 'tank_select' || state.phase === 'stage_select' || state.phase === 'mp_lobby' || state.phase === 'mp_waiting' || state.phase === 'skins_shop';
    this.titleText.visible = !isMenu;
    this.roundText.visible = !isMenu;
    this.fpsText.visible = !isMenu;
    this.windText.visible = !isMenu;
    this.seedText.visible = !isMenu;
    this.biomeText.visible = !isMenu;
    if (isMenu) {
      this.hintsText.visible = false;
      this.turnText.visible = false;
      this.weaponText.visible = false;
      this.shotPanel.hide();
      this.momentumBar.hide();
      this.playerCards.hide();
      this.postMatch.hide();
      this.gameOverText.visible = false;
      this.turnDialog.hide();
      this.notifications.hide();
      this.tankLabels.hide();
      this.multiplayerHud.hide();
      // Coin counter — show in tank select, stage select, mode select, skins shop
      const showCoins = state.phase === 'tank_select' || state.phase === 'stage_select' || state.phase === 'mode_select' || state.phase === 'skins_shop';
      if (showCoins) {
        const coins = getCoins();
        this.coinText.text = `${coins}`;
        this.coinText.visible = true;
      } else {
        this.coinText.visible = false;
      }
      return;
    }
    this.coinText.visible = false;

    // --- Composition class updates ---
    this.tankLabels.update(state, dt);
    this.notifications.update(state, dt);
    this.multiplayerHud.update(state);
    this.floatingText.update(dt);
    this.playerCards.update(state, dt);

    // Momentum bar + shot panel — standalone renderers, not replaced by cards
    this.momentumBar.update(state, dt);
    this.shotPanel.update(state, dt);

    // --- Round counter (screen-space) ---
    if (state.modeState.mode === 'training') {
      this.roundText.text = 'WEAPON LAB';
    } else if (state.modeState.mode === 'survival') {
      const kills = state.modeState.survivalKills;
      const hs = state.modeState.survivalHighScore;
      if (hs) {
        this.roundText.text = `Kills: ${kills}  (Best: ${hs.kills})`;
      } else {
        this.roundText.text = `Kills: ${kills}`;
      }
      this.roundText.style.fill = 0xff4444;
    } else if (state.modeState.mode === 'target_blast') {
      const remaining = state.modeState.targets.filter((t: { hit: boolean }) => !t.hit).length;
      const total = state.modeState.targets.length;
      const time = state.modeState.elapsedTime.toFixed(1);
      if (state.modeState.runComplete) {
        this.roundText.text = `COMPLETE! ${time}s / ${state.modeState.shotsFired} shots`;
      } else if (state.modeState.allTargetsHit) {
        this.roundText.text = `Find the exit!  ${state.modeState.shotsFired} shots / ${time}s`;
        this.roundText.style.fill = 0x44ff88;
      } else {
        this.roundText.text = `Targets: ${total - remaining}/${total}  Shots: ${state.modeState.shotsFired}  Time: ${time}s`;
        this.roundText.style.fill = 0xffffff;
      }
    } else {
      this.roundText.text = `Round ${state.roundNumber} - Turn ${state.turnNumber}`;
    }

    // --- Terrain info ---
    this.seedText.text = `SEED: ${seedToDisplay(state.terrainSeed)}`;
    const biomeLabel = state.terrainFeature
      ? `${state.terrainBiome.toUpperCase()} + ${state.terrainFeature.toUpperCase()}`
      : state.terrainBiome.toUpperCase();
    this.biomeText.text = biomeLabel;

    // Control hints removed — kept hidden
    this.hintsText.visible = false;

    // --- Turn indicator ---
    if (state.phase === 'aiming' || state.phase === 'firing' || state.phase === 'lottery_spin' || state.phase === 'lottery_pick') {
      if (state.modeState.mode === 'training') {
        this.turnText.text = 'WEAPON LAB';
        this.turnText.style.fill = 0x44ff88;
      } else if (state.modeState.mode === 'target_blast') {
        if (state.modeState.allTargetsHit && !state.modeState.runComplete) {
          this.turnText.text = 'FIND THE EXIT!';
          this.turnText.style.fill = 0x44ff88;
        } else {
          this.turnText.text = 'FIRE AT WILL';
          this.turnText.style.fill = 0xffaa00;
        }
      } else {
        const currentP = state.players[state.currentPlayer];
        const color = PLAYER_COLORS[state.currentPlayer];
        this.turnText.text = `${currentP.name} TURN`;
        this.turnText.style.fill = color;
      }
      this.turnText.visible = true;

      // Detect turn change → trigger punch
      if (state.currentPlayer !== this.prevTurnPlayer && this.prevTurnPlayer >= 0) {
        this.turnPunchTimer = PLAYER_CARD.TURN_PUNCH_DURATION;
      }
      this.prevTurnPlayer = state.currentPlayer;
    } else if (state.phase === 'death_sequence' || state.phase === 'awaiting_next_turn') {
      this.turnText.visible = true;
    } else {
      this.turnText.visible = false;
    }

    // Turn punch animation (scale 1.2x → 1.0x)
    if (this.turnPunchTimer > 0) {
      this.turnPunchTimer = Math.max(0, this.turnPunchTimer - dt);
      const t = this.turnPunchTimer / PLAYER_CARD.TURN_PUNCH_DURATION;
      const scale = 1 + (PLAYER_CARD.TURN_PUNCH_SCALE - 1) * t;
      this.turnText.scale.set(scale);
    } else {
      this.turnText.scale.set(1);
    }

    // --- Weapon name ---
    const currentWeapon = state.players[state.currentPlayer]?.selectedWeapon;
    if (currentWeapon && (state.phase === 'aiming' || state.phase === 'firing')) {
      if (modeUsesShotTiers(state.modeState.mode)) {
        this.weaponText.visible = false;
      } else {
        this.weaponText.text = currentWeapon.name.toUpperCase();
        const rarityColorMap: Record<string, number> = {
          common: LOTTERY.COLOR_COMMON,
          uncommon: LOTTERY.COLOR_UNCOMMON,
          rare: LOTTERY.COLOR_RARE,
          epic: LOTTERY.COLOR_EPIC,
          legendary: LOTTERY.COLOR_LEGENDARY,
        };
        this.weaponText.style.fill = rarityColorMap[currentWeapon.rarity] ?? 0xffffff;
        this.weaponText.visible = true;
      }
    } else {
      this.weaponText.visible = false;
    }

    // --- Game over / Post-match summary ---
    if (state.phase === 'game_over') {
      // Post-match summary replaces the old bare text
      this.postMatch.update(state, dt);
      this.gameOverText.visible = false; // hidden — post-match handles winner display

      // Multiplayer HUD still handles connection-level prompts
      if (state.multiplayer?.enabled) {
        this.multiplayerHud.updateGameOver(state);
      } else {
        this.multiplayerHud.hideGameOverPrompt();
      }
    } else {
      this.postMatch.hide();
      this.gameOverText.visible = false;
      this.multiplayerHud.hideGameOverPrompt();
    }

    // --- Weapon Lab hit readout ---
    if (state.modeState.mode === 'training' && state.modeState.labLastHit && !isMenu) {
      const h = state.modeState.labLastHit;
      this.labReadout.visible = true;
      this.labReadout.text = `LAST HIT\nweapon: ${h.weaponName}\ntier:   ${h.tier}\ndmg:    ${h.damage.toFixed(1)}%\ntotal:  ${h.totalDamage.toFixed(1)}%\nkb:     ${h.knockbackMag.toFixed(1)} @ ${h.knockbackAngle.toFixed(0)}\u00B0\nblast:  ${h.blastRadius}px`;
    } else {
      this.labReadout.visible = false;
    }

    // --- FPS counter ---
    this.fpsFrames++;
    this.fpsElapsed += dt;
    if (this.fpsElapsed >= 0.5) {
      this.fpsDisplay = Math.round(this.fpsFrames / this.fpsElapsed);
      this.fpsFrames = 0;
      this.fpsElapsed = 0;
    }
    const frozen = state.frameCount < state.freezeUntil;
    this.fpsText.text = frozen ? `FPS: ${this.fpsDisplay} [FREEZE]` : `FPS: ${this.fpsDisplay}`;
    this.fpsText.style.fill = frozen ? 0xff4444 : 0x888888;

    // --- Wind indicator ---
    const windInfo = formatWind(state.wind);
    this.windText.text = `WIND: ${windInfo.display}`;
    if (!windInfo.isCalm) {
      const pulse = 0.6 + Math.sin(Date.now() * 0.003) * 0.4;
      this.windText.alpha = pulse;
    } else {
      this.windText.alpha = 0.6;
    }
  }

  destroy(): void {
    this.tankLabels.destroy();
    this.floatingText.destroy();
    this.turnDialog.destroy();
    this.notifications.destroy();
    this.multiplayerHud.destroy();
    this.momentumBar.destroy();
    this.shotPanel.destroy();
    this.playerCards.destroy();
    this.postMatch.destroy();
    this.titleText.destroy();
    this.roundText.destroy();
    this.hintsText.destroy();
    this.turnText.destroy();
    this.weaponText.destroy();
    this.gameOverText.destroy();
    this.windText.destroy();
    this.seedText.destroy();
    this.biomeText.destroy();
    this.labReadout.destroy();
    this.fpsText.destroy();
    this.coinText.destroy();
  }
}
